import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

function PostCard({ post, themeSlug }) {
  const dateStr = new Date(post.date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <Link
      to={`/blog/${themeSlug}/${post.slug}`}
      className="block bg-surface rounded-clay shadow-clay dark:shadow-clay-dark border border-surface p-6 hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all duration-200 group"
    >
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
        {post.title}
      </h3>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-3">
        {post.seo_description}
      </p>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          {(post.tags || []).slice(0, 3).map(tag => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-neutral-100/80 dark:bg-white/10 text-neutral-600 dark:text-neutral-300"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
          <span>{post.read_time} min</span>
          <span>·</span>
          <span>{dateStr}</span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogTheme() {
  const { theme: themeSlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [themeInfo, setThemeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`/api/blog/posts/?theme=${themeSlug}`).then(r => r.json()),
      fetch('/api/blog/themes/').then(r => r.json()),
    ])
      .then(([postList, themes]) => {
        const found = themes.find(t => t.slug === themeSlug);
        if (!found) { setError('Topic not found'); return; }
        setThemeInfo(found);
        setPosts(postList);
      })
      .catch(() => setError('Failed to load articles'))
      .finally(() => setLoading(false));
  }, [themeSlug]);

  return (
    <>
      {themeInfo && (
        <SEOHead
          title={`${themeInfo.name} — Options Trading Guides`}
          description={themeInfo.description}
          canonicalPath={`/blog/${themeSlug}`}
        />
      )}

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/blog" className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All Topics
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <>
            <div className="py-10">
              <div className="h-9 w-64 bg-neutral-100 dark:bg-white/8 rounded animate-pulse mb-3" />
              <div className="h-4 w-96 bg-neutral-100 dark:bg-white/8 rounded animate-pulse" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-surface rounded-clay h-40 animate-pulse shadow-clay dark:shadow-clay-dark" />
              ))}
            </div>
          </>
        ) : error ? (
          <div className="py-24 text-center">
            <p className="text-gray-500 dark:text-gray-400">{error}</p>
            <Link to="/blog" className="mt-4 inline-block text-primary-600 dark:text-primary-400 hover:underline">
              ← Back to Blog
            </Link>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="py-10">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {themeInfo.name}
              </h1>
              <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-2xl">
                {themeInfo.description}
              </p>
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                {posts.length} {posts.length === 1 ? 'article' : 'articles'}
              </p>
            </div>

            {/* Posts */}
            {posts.length === 0 ? (
              <p className="text-gray-400 dark:text-gray-500 text-center py-16">No articles yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {posts.map(post => (
                  <PostCard key={post.slug} post={post} themeSlug={themeSlug} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
